import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatBRDateShort } from "../utils/date";
import { stripTrailingNumber } from "../utils/lodgeName";
import {
  Compass,
  Landmark,
  MapPin,
  Mail,
  Phone,
  Users,
  CalendarCheck,
  UserPlus,
  ArrowRight,
  Sparkles,
  Loader2,
  CheckCircle2,
  Send,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LojaPublicPage = () => {
  const { lojaNumber } = useParams();
  const navigate = useNavigate();
  const [loja, setLoja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    profession: "",
    address: "",
    message: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/lojas/public/${lojaNumber}`);
        setLoja(res.data);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lojaNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Preencha nome, e-mail e telefone.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/lojas/public/${lojaNumber}/candidatar-se`, form);
      setSuccess(res.data);
      toast.success("Manifestação enviada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar manifestação.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6">
        <div className="text-center">
          <Compass className="w-14 h-14 text-slate-500 mx-auto mb-4" />
          <h1 className="text-2xl font-serif font-bold text-white">Loja não encontrada</h1>
          <p className="text-sm text-slate-400 mt-2">Verifique o número da Loja na URL.</p>
          <button onClick={() => navigate("/")} className="mt-4 text-[11px] uppercase font-bold text-[#D4AF37] hover:text-[#F5C542] tracking-widest">
            Voltar ao Sistema →
          </button>
        </div>
      </div>
    );
  }

  const lojaPrefix = loja.name.startsWith("A.R.L.S.") ? "" : "A.R.L.S. ";
  const cleanLojaName = stripTrailingNumber(loja.name).replace(/^A\.R\.L\.S\.\s*/i, "");

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#080C14] via-[#0B111E] to-[#0F172A] text-slate-100" data-testid="loja-public-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#D4AF37]/20">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 30% 20%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 70% 60%, #D4AF37 1px, transparent 1px)",
          backgroundSize: "80px 80px, 120px 120px",
        }}></div>
        <div className="max-w-5xl mx-auto px-6 py-16 relative">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Brasão */}
            <div className="shrink-0">
              {loja.logo_b64 ? (
                <div className="w-32 h-32 md:w-40 md:h-40 bg-white rounded-2xl flex items-center justify-center overflow-hidden shadow-[0_0_40px_rgba(212,175,55,0.35)] border-4 border-[#D4AF37]/40">
                  <img src={loja.logo_b64} alt="Brasão" className="w-full h-full object-contain p-3" data-testid="loja-public-logo" />
                </div>
              ) : (
                <div className="w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-[#D4AF37] to-[#8B7513] rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.35)]">
                  <Compass className="w-16 h-16 md:w-20 md:h-20 text-slate-950" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="text-center md:text-left flex-1">
              <div className="text-[11px] tracking-widest uppercase text-[#D4AF37] font-bold mb-2">
                À Glória do Grande Arquiteto do Universo
              </div>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white leading-tight">
                {lojaPrefix}
                <span className="text-[#D4AF37]">{cleanLojaName}</span>
              </h1>
              <div className="mt-2 flex flex-wrap items-center justify-center md:justify-start gap-x-3 gap-y-1 text-sm text-slate-300">
                <span className="font-mono text-[#D4AF37]">nº {loja.number}</span>
                <span className="text-slate-600">•</span>
                <span>Or∴ {loja.oriente}</span>
                {loja.obediencia && (
                  <>
                    <span className="text-slate-600">•</span>
                    <span className="text-slate-400 text-xs">{loja.obediencia}</span>
                  </>
                )}
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center md:justify-start gap-3">
                <button
                  data-testid="loja-public-btn-cta"
                  onClick={() => setShowForm(true)}
                  className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-3 rounded-xl text-xs uppercase tracking-widest transition-all shadow-[0_0_25px_rgba(212,175,55,0.35)]"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Quero me candidatar</span>
                </button>
                <a
                  href="#agenda"
                  className="flex items-center space-x-2 bg-transparent border border-slate-700 hover:border-[#D4AF37]/60 text-slate-200 hover:text-white font-semibold px-5 py-3 rounded-xl text-xs uppercase tracking-widest transition-all"
                >
                  <CalendarCheck className="w-4 h-4" />
                  <span>Ver Agenda</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats + Contact */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#111927] border border-slate-800 rounded-2xl p-5">
            <div className="text-[10px] uppercase text-slate-400 tracking-wider font-bold flex items-center space-x-1"><Users className="w-3 h-3" /><span>Quadro de Obreiros</span></div>
            <div className="text-3xl font-serif font-bold text-[#D4AF37] mt-2 font-mono">{loja.stats.total_members}</div>
            <div className="text-xs text-slate-500 mt-0.5">Irmãos ativos</div>
          </div>
          <div className="bg-[#111927] border border-slate-800 rounded-2xl p-5">
            <div className="text-[10px] uppercase text-slate-400 tracking-wider font-bold flex items-center space-x-1"><CalendarCheck className="w-3 h-3" /><span>Sessões Realizadas</span></div>
            <div className="text-3xl font-serif font-bold text-emerald-400 mt-2 font-mono">{loja.stats.total_sessions}</div>
            <div className="text-xs text-slate-500 mt-0.5">Trabalhos litúrgicos</div>
          </div>
          <div className="bg-[#111927] border border-slate-800 rounded-2xl p-5">
            <div className="text-[10px] uppercase text-slate-400 tracking-wider font-bold flex items-center space-x-1"><Landmark className="w-3 h-3" /><span>Fundada em</span></div>
            <div className="text-2xl font-serif font-bold text-purple-300 mt-2 font-mono">{loja.founded ? loja.founded.slice(0, 4) : "—"}</div>
            <div className="text-xs text-slate-500 mt-0.5">Ano de instalação</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {loja.address && (
            <div className="bg-[#080D18] border border-slate-800 rounded-xl p-4 flex items-start space-x-3">
              <MapPin className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-bold tracking-wider">Templo</div>
                <div className="text-xs text-slate-200 mt-0.5">{loja.address}</div>
              </div>
            </div>
          )}
          {loja.email && (
            <div className="bg-[#080D18] border border-slate-800 rounded-xl p-4 flex items-start space-x-3">
              <Mail className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-bold tracking-wider">Chancelaria</div>
                <a href={`mailto:${loja.email}`} className="text-xs text-slate-200 hover:text-[#D4AF37] mt-0.5 block break-all">{loja.email}</a>
              </div>
            </div>
          )}
          {loja.phone && (
            <div className="bg-[#080D18] border border-slate-800 rounded-xl p-4 flex items-start space-x-3">
              <Phone className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-bold tracking-wider">Telefone</div>
                <div className="text-xs text-slate-200 mt-0.5">{loja.phone}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Agenda */}
      <section id="agenda" className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center space-x-2 text-[11px] uppercase tracking-wider text-[#D4AF37] font-bold mb-3">
          <CalendarCheck className="w-3.5 h-3.5" />
          <span>Agenda Litúrgica Pública</span>
        </div>
        <h2 className="text-2xl font-serif font-bold text-white mb-5">Próximos Trabalhos</h2>

        {loja.upcoming_sessions.length === 0 ? (
          <div className="bg-[#111927] border border-slate-800 rounded-2xl p-10 text-center">
            <CalendarCheck className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Nenhuma sessão pública agendada no momento.</p>
            <p className="text-xs text-slate-500 mt-1 italic">Entre em contato com a Chancelaria para saber mais.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {loja.upcoming_sessions.map((s) => (
              <div key={s.id} className="bg-[#111927] border border-slate-800 hover:border-[#D4AF37]/40 rounded-xl p-4 flex items-center justify-between transition-colors">
                <div>
                  <div className="text-[11px] uppercase text-slate-400 font-bold tracking-wider flex items-center space-x-2">
                    <span className="font-mono text-[#D4AF37]">Sessão nº {s.session_number}</span>
                    <span className="text-slate-600">•</span>
                    <span>{s.session_type}</span>
                  </div>
                  <div className="text-sm font-serif font-bold text-white mt-0.5">{s.title}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">Grau exigido: {s.degree_required}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-serif font-bold text-[#D4AF37] font-mono">
                    {formatBRDateShort(s.date)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA final */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-[#111927] to-[#0B111E] border border-[#D4AF37]/30 rounded-2xl p-8 md:p-12 text-center">
          <Sparkles className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-white">Sente o chamado da Arte Real?</h3>
          <p className="text-sm text-slate-300 mt-3 max-w-xl mx-auto leading-relaxed">
            Se você é homem livre e de bons costumes, maior de 21 anos, e deseja trilhar a jornada de auto-conhecimento e fraternidade, manifeste seu interesse. Um Irmão da Loja entrará em contato para conhecer sua história.
          </p>
          <button
            data-testid="loja-public-btn-cta-final"
            onClick={() => setShowForm(true)}
            className="mt-6 inline-flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-8 py-3 rounded-xl text-xs uppercase tracking-widest transition-all shadow-[0_0_25px_rgba(212,175,55,0.35)]"
          >
            <UserPlus className="w-4 h-4" />
            <span>Manifestar Interesse</span>
          </button>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-6 text-center text-[10px] uppercase tracking-widest text-slate-500">
        Lojas Integradas • Plataforma de Gestão Maçônica
      </footer>

      {/* Signup Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !success && setShowForm(false)}>
          <div className="w-full max-w-lg bg-[#0B111E] border border-[#D4AF37]/40 rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {success ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-9 h-9 text-emerald-400" />
                </div>
                <h3 className="text-xl font-serif font-bold text-white">Manifestação Recebida!</h3>
                <p className="text-sm text-slate-300 mt-3 leading-relaxed">{success.message}</p>
                <div className="text-[10px] text-slate-500 mt-4 font-mono">Ref: {success.reference_id}</div>
                <button
                  data-testid="loja-public-btn-close-success"
                  onClick={() => { setShowForm(false); setSuccess(null); setForm({ name: "", email: "", phone: "", profession: "", address: "", message: "" }); }}
                  className="mt-6 text-[10px] uppercase font-bold text-[#D4AF37] hover:text-[#F5C542] tracking-widest"
                >
                  Fechar →
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col overflow-y-auto">
                <div className="px-6 py-5 border-b border-slate-800 bg-[#080D18]">
                  <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold flex items-center space-x-2">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Manifestação de Interesse</span>
                  </div>
                  <h2 className="text-lg font-serif font-bold text-white mt-1">Candidatar-se à Iniciação</h2>
                  <p className="text-[11px] text-slate-400 mt-1">{lojaPrefix}{cleanLojaName} nº {loja.number}</p>
                </div>

                <div className="p-6 space-y-3">
                  <div>
                    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Nome completo *</label>
                    <input required data-testid="public-cand-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">E-mail *</label>
                      <input required type="email" data-testid="public-cand-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Telefone *</label>
                      <input required data-testid="public-cand-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Profissão</label>
                    <input data-testid="public-cand-profession" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Endereço</label>
                    <input data-testid="public-cand-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Por que deseja se iniciar?</label>
                    <textarea rows={4} data-testid="public-cand-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none resize-none" placeholder="Compartilhe brevemente sua motivação..." />
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-slate-800 bg-[#080D18] flex justify-between items-center">
                  <button type="button" onClick={() => setShowForm(false)} className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold">Cancelar</button>
                  <button data-testid="public-cand-submit" type="submit" disabled={submitting} className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider">
                    {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>Enviando...</span></> : <><Send className="w-3.5 h-3.5" /><span>Enviar Manifestação</span></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
